import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import ImageUpload from './ImageUpload';
import EnhancedRichTextEditor from './EnhancedRichTextEditor';

const AcademicsEdit = () => {
  const [academicsData, setAcademicsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('banner');

  const [formData, setFormData] = useState({
    // Banner Section
    bannerTitle: '',
    bannerSubtitle: '',
    bannerImage: '',
    
    // B.Tech Courses
    btechCourses: [
      { code: '', name: '', description: '' }
    ],
    
    // Polytechnic Courses
    polytechnicCourses: [
      { code: '', name: '', description: '' }
    ],
    
    // Departments
    departments: [
      { id: '', name: '', description: '', labs: [''] }
    ],
    
    // Anti-Ragging Section
    antiRaggingTitle: '',
    antiRaggingDescription: '',
    antiRaggingActs: [''],
    antiRaggingHelpline: '',
    antiRaggingCommittee: [''],
    
    // Grievance Section
    grievanceTitle: '',
    grievanceDescription: '',
    grievanceFormEnabled: true,
    
    // Faculty & Staff Section
    facultyTitle: '',
    facultyDescription: '',
    facultyMembers: [],
    facultyContent: {
      introduction: '',
      sections: [
        { title: '', content: '' }
      ]
    },
    facultyContactInfo: {
      academic: {
        title: '',
        email: '',
        phone: '',
        hours: ''
      },
      administrative: {
        title: '',
        email: '',
        phone: '',
        hours: ''
      }
    },
    
    isActive: true
  });

  useEffect(() => {
    fetchAcademicsData();
  }, []);

  const fetchAcademicsData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAcademicsAdmin();
      const data = response.data;
      
      setAcademicsData(data);
      setFormData({
        bannerTitle: data.bannerTitle || '',
        bannerSubtitle: data.bannerSubtitle || '',
        bannerImage: data.bannerImage || '',
        btechCourses: data.btechCourses || formData.btechCourses,
        polytechnicCourses: data.polytechnicCourses || formData.polytechnicCourses,
        departments: data.departments || formData.departments,
        antiRaggingTitle: data.antiRaggingTitle || '',
        antiRaggingDescription: data.antiRaggingDescription || '',
        antiRaggingActs: data.antiRaggingActs || formData.antiRaggingActs,
        antiRaggingHelpline: data.antiRaggingHelpline || '',
        antiRaggingCommittee: data.antiRaggingCommittee || formData.antiRaggingCommittee,
        grievanceTitle: data.grievanceTitle || '',
        grievanceDescription: data.grievanceDescription || '',
        grievanceFormEnabled: data.grievanceFormEnabled !== undefined ? data.grievanceFormEnabled : true,
        facultyTitle: data.facultyTitle || '',
        facultyDescription: data.facultyDescription || '',
        facultyMembers: data.facultyMembers || [],
        facultyContent: data.facultyContent || formData.facultyContent,
        facultyContactInfo: data.facultyContactInfo || formData.facultyContactInfo,
        isActive: data.isActive !== undefined ? data.isActive : true
      });
    } catch (error) {
      setError('Failed to load academics data');
      console.error('Error fetching academics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedChange = (field, subField, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [subField]: value
      }
    }));
  };

  const handleArrayChange = (field, index, key, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => 
        i === index ? (key === null ? value : { ...item, [key]: value }) : item
      )
    }));
  };

  const handleStringArrayChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const addArrayItem = (field, template) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], template]
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleLabChange = (deptIndex, labIndex, value) => {
    setFormData(prev => ({
      ...prev,
      departments: prev.departments.map((dept, i) => 
        i === deptIndex ? {
          ...dept,
          labs: dept.labs.map((lab, j) => j === labIndex ? value : lab)
        } : dept
      )
    }));
  };

  const addLab = (deptIndex) => {
    setFormData(prev => ({
      ...prev,
      departments: prev.departments.map((dept, i) => 
        i === deptIndex ? {
          ...dept,
          labs: [...dept.labs, '']
        } : dept
      )
    }));
  };

  const removeLab = (deptIndex, labIndex) => {
    setFormData(prev => ({
      ...prev,
      departments: prev.departments.map((dept, i) => 
        i === deptIndex ? {
          ...dept,
          labs: dept.labs.filter((_, j) => j !== labIndex)
        } : dept
      )
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      await apiService.updateAcademics(formData);
      setSuccess('Academics content updated successfully!');
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to save academics content');
      console.error('Error saving academics:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">
          <i className="fas fa-spinner fa-spin mr-2"></i>
          Loading academics data...
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'banner', label: 'Banner', icon: 'fas fa-image' },
    { id: 'courses', label: 'Courses', icon: 'fas fa-graduation-cap' },
    { id: 'departments', label: 'Departments', icon: 'fas fa-building' },
    { id: 'anti-ragging', label: 'Anti-Ragging', icon: 'fas fa-shield-alt' },
    { id: 'grievance', label: 'Grievance', icon: 'fas fa-comment-alt' },
    { id: 'faculty', label: 'Faculty & Staff', icon: 'fas fa-users' }
  ];

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Manage Academics Page</h1>
          <p className="mt-2 text-sm text-gray-600">Update content for all sections of the academics page</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => window.location.href = '/academics'}
            className="btn btn-ghost"
          >
            <i className="fas fa-eye mr-2"></i>
            View Page
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`btn btn-primary ${saving ? 'loading' : ''}`}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success mb-4">
          {success}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors
                ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <i className={`${tab.icon} mr-2`}></i>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6">
          {/* Banner Section */}
          {activeTab === 'banner' && (
            <div className="space-y-6">
              <div className="border-b border-gray-200 pb-4">
                <h3 className="text-lg font-semibold text-gray-900">Banner Settings</h3>
                <p className="mt-1 text-sm text-gray-500">Customize the banner appearance for the academics page</p>
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Banner Title</span>
                </label>
                <input
                  type="text"
                  value={formData.bannerTitle}
                  onChange={(e) => handleInputChange('bannerTitle', e.target.value)}
                  className="input input-bordered"
                  placeholder="Enter banner title"
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Banner Subtitle</span>
                </label>
                <input
                  type="text"
                  value={formData.bannerSubtitle}
                  onChange={(e) => handleInputChange('bannerSubtitle', e.target.value)}
                  className="input input-bordered"
                  placeholder="Enter banner subtitle"
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Banner Image</span>
                </label>
                <ImageUpload
                  onChange={(imageUrl) => handleInputChange('bannerImage', imageUrl)}
                  value={formData.bannerImage}
                  placeholder="Banner image filename"
                />
              </div>
            </div>
          )}

          {/* Courses Section */}
          {activeTab === 'courses' && (
            <div className="space-y-8">
              <div className="border-b border-gray-200 pb-4">
                <h3 className="text-lg font-semibold text-gray-900">Courses Management</h3>
                <p className="mt-1 text-sm text-gray-500">Manage B.Tech and Polytechnic courses offered by the institution</p>
              </div>
              
              {/* B.Tech Courses */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h4 className="text-base font-semibold text-gray-900">B.Tech Courses</h4>
                    <p className="text-sm text-gray-500">4-year undergraduate programs</p>
                  </div>
                  <button
                    onClick={() => addArrayItem('btechCourses', { code: '', name: '', description: '' })}
                    className="btn btn-sm btn-primary"
                  >
                    <i className="fas fa-plus mr-1"></i>
                    Add Course
                  </button>
                </div>
                
                {formData.btechCourses.map((course, index) => (
                  <div key={index} className="bg-white border border-gray-200 p-4 rounded-lg mb-4">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium">Course {index + 1}</span>
                      <button
                        onClick={() => removeArrayItem('btechCourses', index)}
                        className="btn btn-sm btn-error btn-outline"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text text-sm">Course Code</span>
                        </label>
                        <input
                          type="text"
                          value={course.code}
                          onChange={(e) => handleArrayChange('btechCourses', index, 'code', e.target.value)}
                          className="input input-sm input-bordered"
                          placeholder="e.g., CSE"
                        />
                      </div>
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text text-sm">Course Name</span>
                        </label>
                        <input
                          type="text"
                          value={course.name}
                          onChange={(e) => handleArrayChange('btechCourses', index, 'name', e.target.value)}
                          className="input input-sm input-bordered"
                          placeholder="e.g., Computer Science & Engineering"
                        />
                      </div>
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text text-sm">Description</span>
                        </label>
                        <input
                          type="text"
                          value={course.description}
                          onChange={(e) => handleArrayChange('btechCourses', index, 'description', e.target.value)}
                          className="input input-sm input-bordered"
                          placeholder="Course description"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Polytechnic Courses */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h4 className="text-base font-semibold text-gray-900">Polytechnic Courses</h4>
                    <p className="text-sm text-gray-500">3-year diploma programs</p>
                  </div>
                  <button
                    onClick={() => addArrayItem('polytechnicCourses', { code: '', name: '', description: '' })}
                    className="btn btn-sm btn-primary"
                  >
                    <i className="fas fa-plus mr-1"></i>
                    Add Course
                  </button>
                </div>
                
                {formData.polytechnicCourses.map((course, index) => (
                  <div key={index} className="bg-white border border-gray-200 p-4 rounded-lg mb-4">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium">Course {index + 1}</span>
                      <button
                        onClick={() => removeArrayItem('polytechnicCourses', index)}
                        className="btn btn-sm btn-error btn-outline"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text text-sm">Course Code</span>
                        </label>
                        <input
                          type="text"
                          value={course.code}
                          onChange={(e) => handleArrayChange('polytechnicCourses', index, 'code', e.target.value)}
                          className="input input-sm input-bordered"
                          placeholder="e.g., ME"
                        />
                      </div>
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text text-sm">Course Name</span>
                        </label>
                        <input
                          type="text"
                          value={course.name}
                          onChange={(e) => handleArrayChange('polytechnicCourses', index, 'name', e.target.value)}
                          className="input input-sm input-bordered"
                          placeholder="e.g., Mechanical Engineering"
                        />
                      </div>
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text text-sm">Description</span>
                        </label>
                        <input
                          type="text"
                          value={course.description}
                          onChange={(e) => handleArrayChange('polytechnicCourses', index, 'description', e.target.value)}
                          className="input input-sm input-bordered"
                          placeholder="Course description"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Departments Section */}
          {activeTab === 'departments' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center border-b border-gray-200 pb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Departments</h3>
                  <p className="mt-1 text-sm text-gray-500">Manage academic departments and their facilities</p>
                </div>
                <button
                  onClick={() => addArrayItem('departments', { id: '', name: '', description: '', labs: [''] })}
                  className="btn btn-primary"
                >
                  <i className="fas fa-plus mr-1"></i>
                  Add Department
                </button>
              </div>
              
              {formData.departments.map((dept, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h4 className="font-semibold">Department {index + 1}</h4>
                    <button
                      onClick={() => removeArrayItem('departments', index)}
                      className="btn btn-sm btn-error btn-outline"
                    >
                      Remove Department
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Department ID</span>
                      </label>
                      <input
                        type="text"
                        value={dept.id}
                        onChange={(e) => handleArrayChange('departments', index, 'id', e.target.value)}
                        className="input input-bordered"
                        placeholder="e.g., cse"
                      />
                    </div>
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Department Name</span>
                      </label>
                      <input
                        type="text"
                        value={dept.name}
                        onChange={(e) => handleArrayChange('departments', index, 'name', e.target.value)}
                        className="input input-bordered"
                        placeholder="e.g., Department of Computer Science & Engineering"
                      />
                    </div>
                  </div>
                  
                  <div className="form-control mb-4">
                    <label className="label">
                      <span className="label-text">Description</span>
                    </label>
                    <EnhancedRichTextEditor
                      value={dept.description}
                      onChange={(value) => handleArrayChange('departments', index, 'description', value)}
                      placeholder="Department description"
                    />
                  </div>
                  
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="label">
                        <span className="label-text">Laboratory Facilities</span>
                      </label>
                      <button
                        onClick={() => addLab(index)}
                        className="btn btn-sm btn-outline"
                      >
                        Add Lab
                      </button>
                    </div>
                    {dept.labs.map((lab, labIndex) => (
                      <div key={labIndex} className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={lab}
                          onChange={(e) => handleLabChange(index, labIndex, e.target.value)}
                          className="input input-sm input-bordered flex-1"
                          placeholder="Lab name"
                        />
                        <button
                          onClick={() => removeLab(index, labIndex)}
                          className="btn btn-sm btn-error btn-outline"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Anti-Ragging Section */}
          {activeTab === 'anti-ragging' && (
            <div className="space-y-6">
              <div className="border-b border-gray-200 pb-4">
                <h3 className="text-lg font-semibold text-gray-900">Anti-Ragging Policy</h3>
                <p className="mt-1 text-sm text-gray-500">Configure anti-ragging policy information and committee details</p>
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Section Title</span>
                </label>
                <input
                  type="text"
                  value={formData.antiRaggingTitle}
                  onChange={(e) => handleInputChange('antiRaggingTitle', e.target.value)}
                  className="input input-bordered"
                  placeholder="Anti-Ragging Policy"
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Description</span>
                </label>
                <textarea
                  value={formData.antiRaggingDescription}
                  onChange={(e) => handleInputChange('antiRaggingDescription', e.target.value)}
                  className="textarea textarea-bordered"
                  rows="3"
                  placeholder="Policy description"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="label">
                    <span className="label-text">Acts that Constitute Ragging</span>
                  </label>
                  <button
                    onClick={() => addArrayItem('antiRaggingActs', '')}
                    className="btn btn-sm btn-outline"
                  >
                    Add Act
                  </button>
                </div>
                {formData.antiRaggingActs.map((act, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <textarea
                      value={act}
                      onChange={(e) => handleStringArrayChange('antiRaggingActs', index, e.target.value)}
                      className="textarea textarea-sm textarea-bordered flex-1"
                      rows="2"
                      placeholder="Describe the act"
                    />
                    <button
                      onClick={() => removeArrayItem('antiRaggingActs', index)}
                      className="btn btn-sm btn-error btn-outline"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Helpline Information</span>
                </label>
                <input
                  type="text"
                  value={formData.antiRaggingHelpline}
                  onChange={(e) => handleInputChange('antiRaggingHelpline', e.target.value)}
                  className="input input-bordered"
                  placeholder="24/7 Anti-Ragging Helpline"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="label">
                    <span className="label-text">Committee Members</span>
                  </label>
                  <button
                    onClick={() => addArrayItem('antiRaggingCommittee', '')}
                    className="btn btn-sm btn-outline"
                  >
                    Add Member
                  </button>
                </div>
                {formData.antiRaggingCommittee.map((member, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={member}
                      onChange={(e) => handleStringArrayChange('antiRaggingCommittee', index, e.target.value)}
                      className="input input-sm input-bordered flex-1"
                      placeholder="Committee member role"
                    />
                    <button
                      onClick={() => removeArrayItem('antiRaggingCommittee', index)}
                      className="btn btn-sm btn-error btn-outline"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Grievance Section */}
          {activeTab === 'grievance' && (
            <div className="space-y-6">
              <div className="border-b border-gray-200 pb-4">
                <h3 className="text-lg font-semibold text-gray-900">Student Grievance</h3>
                <p className="mt-1 text-sm text-gray-500">Manage student grievance system settings</p>
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Section Title</span>
                </label>
                <input
                  type="text"
                  value={formData.grievanceTitle}
                  onChange={(e) => handleInputChange('grievanceTitle', e.target.value)}
                  className="input input-bordered"
                  placeholder="Student Grievance"
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Description</span>
                </label>
                <textarea
                  value={formData.grievanceDescription}
                  onChange={(e) => handleInputChange('grievanceDescription', e.target.value)}
                  className="textarea textarea-bordered"
                  rows="3"
                  placeholder="Grievance description"
                />
              </div>

              <div className="form-control">
                <label className="cursor-pointer label">
                  <span className="label-text">Enable Grievance Form</span>
                  <input
                    type="checkbox"
                    checked={formData.grievanceFormEnabled}
                    onChange={(e) => handleInputChange('grievanceFormEnabled', e.target.checked)}
                    className="checkbox checkbox-primary"
                  />
                </label>
              </div>
            </div>
          )}

          {/* Faculty Section */}
          {activeTab === 'faculty' && (
            <div className="space-y-6">
              <div className="border-b border-gray-200 pb-4">
                <h3 className="text-lg font-semibold text-gray-900">Faculty & Staff</h3>
                <p className="mt-1 text-sm text-gray-500">Update faculty information and contact details</p>
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Section Title</span>
                </label>
                <input
                  type="text"
                  value={formData.facultyTitle}
                  onChange={(e) => handleInputChange('facultyTitle', e.target.value)}
                  className="input input-bordered"
                  placeholder="Faculty & Staff"
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Description</span>
                </label>
                <textarea
                  value={formData.facultyDescription}
                  onChange={(e) => handleInputChange('facultyDescription', e.target.value)}
                  className="textarea textarea-bordered"
                  rows="3"
                  placeholder="Faculty description"
                />
              </div>

              {/* Faculty Members List */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h4 className="text-base font-semibold text-gray-900">Faculty Members</h4>
                    <p className="text-sm text-gray-500">Add and manage faculty members</p>
                  </div>
                  <button
                    onClick={() => addArrayItem('facultyMembers', { name: '', department: '', designation: '' })}
                    className="btn btn-sm btn-primary"
                  >
                    <i className="fas fa-plus mr-1"></i>
                    Add Faculty
                  </button>
                </div>

                {formData.facultyMembers.length === 0 && (
                  <p className="text-sm text-gray-400 italic">No faculty members added yet. Click "Add Faculty" to add one.</p>
                )}

                {formData.facultyMembers.map((member, index) => (
                  <div key={index} className="bg-white border border-gray-200 p-4 rounded-lg mb-4">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium">Faculty {index + 1}</span>
                      <button
                        onClick={() => removeArrayItem('facultyMembers', index)}
                        className="btn btn-sm btn-error btn-outline"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text text-sm">Faculty Name</span>
                        </label>
                        <input
                          type="text"
                          value={member.name}
                          onChange={(e) => handleArrayChange('facultyMembers', index, 'name', e.target.value)}
                          className="input input-sm input-bordered"
                          placeholder="e.g., Dr. Rajesh Kumar"
                        />
                      </div>
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text text-sm">Department</span>
                        </label>
                        <input
                          type="text"
                          value={member.department}
                          onChange={(e) => handleArrayChange('facultyMembers', index, 'department', e.target.value)}
                          className="input input-sm input-bordered"
                          placeholder="e.g., Computer Science"
                        />
                      </div>
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text text-sm">Designation</span>
                        </label>
                        <input
                          type="text"
                          value={member.designation}
                          onChange={(e) => handleArrayChange('facultyMembers', index, 'designation', e.target.value)}
                          className="input input-sm input-bordered"
                          placeholder="e.g., Professor & HOD"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Introduction</span>
                </label>
                <EnhancedRichTextEditor
                  value={formData.facultyContent.introduction}
                  onChange={(value) => handleNestedChange('facultyContent', 'introduction', value)}
                  placeholder="Faculty introduction content"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="label">
                    <span className="label-text">Content Sections</span>
                  </label>
                  <button
                    onClick={() => {
                      const newSections = [...formData.facultyContent.sections, { title: '', content: '' }];
                      handleNestedChange('facultyContent', 'sections', newSections);
                    }}
                    className="btn btn-outline"
                  >
                    Add Section
                  </button>
                </div>
                {formData.facultyContent.sections.map((section, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-6 mb-4">
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="font-semibold">Section {index + 1}</h4>
                      <button
                        onClick={() => {
                          const newSections = formData.facultyContent.sections.filter((_, i) => i !== index);
                          handleNestedChange('facultyContent', 'sections', newSections);
                        }}
                        className="btn btn-sm btn-error btn-outline"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="form-control mb-4">
                      <label className="label">
                        <span className="label-text">Title</span>
                      </label>
                      <input
                        type="text"
                        value={section.title}
                        onChange={(e) => {
                          const newSections = formData.facultyContent.sections.map((sec, i) =>
                            i === index ? { ...sec, title: e.target.value } : sec
                          );
                          handleNestedChange('facultyContent', 'sections', newSections);
                        }}
                        className="input input-bordered"
                        placeholder="Section title"
                      />
                    </div>
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Content</span>
                      </label>
                      <EnhancedRichTextEditor
                        value={section.content}
                        onChange={(value) => {
                          const newSections = formData.facultyContent.sections.map((sec, i) =>
                            i === index ? { ...sec, content: value } : sec
                          );
                          handleNestedChange('facultyContent', 'sections', newSections);
                        }}
                        placeholder="Section content"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-semibold mb-4">Academic Contact Info</h4>
                  <div className="space-y-3">
                    <div className="form-control">
                      <input
                        type="text"
                        value={formData.facultyContactInfo.academic.title}
                        onChange={(e) => {
                          const newContactInfo = {
                            ...formData.facultyContactInfo,
                            academic: { ...formData.facultyContactInfo.academic, title: e.target.value }
                          };
                          handleInputChange('facultyContactInfo', newContactInfo);
                        }}
                        className="input input-sm input-bordered"
                        placeholder="Contact title"
                      />
                    </div>
                    <div className="form-control">
                      <input
                        type="email"
                        value={formData.facultyContactInfo.academic.email}
                        onChange={(e) => {
                          const newContactInfo = {
                            ...formData.facultyContactInfo,
                            academic: { ...formData.facultyContactInfo.academic, email: e.target.value }
                          };
                          handleInputChange('facultyContactInfo', newContactInfo);
                        }}
                        className="input input-sm input-bordered"
                        placeholder="Email address"
                      />
                    </div>
                    <div className="form-control">
                      <input
                        type="text"
                        value={formData.facultyContactInfo.academic.phone}
                        onChange={(e) => {
                          const newContactInfo = {
                            ...formData.facultyContactInfo,
                            academic: { ...formData.facultyContactInfo.academic, phone: e.target.value }
                          };
                          handleInputChange('facultyContactInfo', newContactInfo);
                        }}
                        className="input input-sm input-bordered"
                        placeholder="Phone number"
                      />
                    </div>
                    <div className="form-control">
                      <input
                        type="text"
                        value={formData.facultyContactInfo.academic.hours}
                        onChange={(e) => {
                          const newContactInfo = {
                            ...formData.facultyContactInfo,
                            academic: { ...formData.facultyContactInfo.academic, hours: e.target.value }
                          };
                          handleInputChange('facultyContactInfo', newContactInfo);
                        }}
                        className="input input-sm input-bordered"
                        placeholder="Office hours"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-semibold mb-4">Administrative Contact Info</h4>
                  <div className="space-y-3">
                    <div className="form-control">
                      <input
                        type="text"
                        value={formData.facultyContactInfo.administrative.title}
                        onChange={(e) => {
                          const newContactInfo = {
                            ...formData.facultyContactInfo,
                            administrative: { ...formData.facultyContactInfo.administrative, title: e.target.value }
                          };
                          handleInputChange('facultyContactInfo', newContactInfo);
                        }}
                        className="input input-sm input-bordered"
                        placeholder="Contact title"
                      />
                    </div>
                    <div className="form-control">
                      <input
                        type="email"
                        value={formData.facultyContactInfo.administrative.email}
                        onChange={(e) => {
                          const newContactInfo = {
                            ...formData.facultyContactInfo,
                            administrative: { ...formData.facultyContactInfo.administrative, email: e.target.value }
                          };
                          handleInputChange('facultyContactInfo', newContactInfo);
                        }}
                        className="input input-sm input-bordered"
                        placeholder="Email address"
                      />
                    </div>
                    <div className="form-control">
                      <input
                        type="text"
                        value={formData.facultyContactInfo.administrative.phone}
                        onChange={(e) => {
                          const newContactInfo = {
                            ...formData.facultyContactInfo,
                            administrative: { ...formData.facultyContactInfo.administrative, phone: e.target.value }
                          };
                          handleInputChange('facultyContactInfo', newContactInfo);
                        }}
                        className="input input-sm input-bordered"
                        placeholder="Phone number"
                      />
                    </div>
                    <div className="form-control">
                      <input
                        type="text"
                        value={formData.facultyContactInfo.administrative.hours}
                        onChange={(e) => {
                          const newContactInfo = {
                            ...formData.facultyContactInfo,
                            administrative: { ...formData.facultyContactInfo.administrative, hours: e.target.value }
                          };
                          handleInputChange('facultyContactInfo', newContactInfo);
                        }}
                        className="input input-sm input-bordered"
                        placeholder="Office hours"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end pt-6 border-t border-gray-200">
            <div className="flex gap-2">
              <div className="form-control">
                <label className="cursor-pointer label">
                  <span className="label-text mr-2">Active</span>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    className="checkbox checkbox-primary"
                  />
                </label>
              </div>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`btn btn-primary ${saving ? 'loading' : ''}`}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcademicsEdit;